import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FileSignature, Printer } from "lucide-react"
import { useEffect, useState } from "react"

import { api, pedir } from "@/api/cliente"
import { comoSeLlama, type Empresa } from "@/componentes/empresa/tipos"
import { PliegoImpresion } from "@/componentes/impresos/PliegoImpresion"
import {
  abrirPdf,
  imprimirPdf,
  pdfDePosicionamiento,
  soltarImpresion,
} from "@/componentes/plantillas/imprimir"
import { RegistroImpresiones } from "@/componentes/plantillas/RegistroImpresiones"
import type {
  DatosPlantilla,
  DatosPosicionamiento,
  Impresion,
  TipoPlantilla,
} from "@/componentes/plantillas/tipos"
import { ETIQUETAS, plantillaEnBlanco } from "@/componentes/plantillas/tipos"
import { VistaPlantilla } from "@/componentes/plantillas/VistaPlantilla"
import { Boton } from "@/componentes/ui/Boton"
import { comoFechaHora } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * La biblioteca de planillas: los formatos que la agencia rellena e imprime.
 *
 * ⚠ **Se escribe DIRECTO sobre el formato.** Era la elección que quedaba abierta —un
 * formulario aparte que luego se vuelca, o la hoja misma— y ésta es la razón: un
 * formulario al lado obliga a mirar en dos sitios y a confiar en que la etiqueta de la
 * izquierda corresponde a la casilla de la derecha. Sobre el formato, la casilla donde
 * se escribe ES la casilla que se imprime, que es la regla que ya sostienen la
 * rotulación y los carnets llevada hasta el final. Y en un papel que se entrega en
 * ventanilla, cualquier diferencia entre lo que se ve y lo que sale es un viaje
 * perdido al puerto.
 *
 * ⚠ **Cada impresión de la SOLICITUD DE POSICIONAMIENTO queda registrada con su PDF.**
 * «Imprimir» compone el PDF —la página original del SENIAT con lo tecleado encima—, lo
 * guarda en el servidor y saca por la impresora ESE MISMO archivo. **Sin registro no
 * hay papel**: si el servidor no lo guarda, no se imprime. Lo contrario dejaría
 * solicitudes entregadas en ventanilla de las que el sistema no sabe nada, que es lo
 * que el registro viene a impedir. «Vehículos» todavía se imprime desde la pantalla y
 * sin registro.
 *
 * ⚠ **Se precarga lo que el sistema sabe con CERTEZA y nada más.** La fecha de
 * solicitud es hoy, la agencia de aduana es la razón social de la empresa y el
 * responsable es quien tiene la sesión abierta. Todo lo demás —DUA, BL, mercancía—
 * lo sabe quien rellena; adivinarlo sería poner en un documento oficial un dato que
 * nadie confirmó.
 *
 * ⚠ **El logotipo del SENIAT se reproduce, no se redibuja.** Es la marca de un
 * organismo del Estado y sale del PDF que publica la propia aduana. Ver
 * `CabeceraSeniat`.
 *
 * ⚠ **No todas las planillas son de la aduana.** La GUÍA DE DESPACHO es papelería de
 * SETIECA —la que sale con el transportista— y por eso lleva la marca, el vino y el
 * filete dorado de la agencia, al revés que los dos formatos oficiales. Y es CARTA,
 * no A4: el papel va por formato (`tipos.ts::PAPEL`) y arrastra su `@page` con
 * nombre, porque con el papel del otro una hoja se parte en dos.
 */
export function Plantillas() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()

  const empresa = useQuery({
    queryKey: ["empresa"],
    queryFn: () => api.get<Empresa>("/api/v1/empresa"),
  })

  /**
   * Lo que el sistema sabe con certeza, puesto en la hoja recién nacida.
   *
   * El nombre de la sesión está disponible en el primer render —Zustand es
   * síncrono—; la razón social llega por la API y la pone el efecto de abajo.
   */
  function enBlanco(tipo: TipoPlantilla): DatosPlantilla {
    const base = plantillaEnBlanco(tipo)
    // ⚠ La guía no tiene bloque de responsable: el del formato oficial es del SENIAT.
    // Quien la firma lo hace a mano al entregarla.
    if (base.tipo === "GUIA_DESPACHO") return base
    return { ...base, responsable: { ...base.responsable, nombre: usuario?.nombre ?? "" } }
  }

  const [datos, setDatos] = useState<DatosPlantilla>(() => enBlanco("POSICIONAMIENTO"))
  // El papel decide la `@page` de la ruta entera: ver la nota de `globales.css`.
  const esGuia = datos.tipo === "GUIA_DESPACHO"

  /**
   * La agencia de aduana, cuando llegue la configuración de la empresa.
   *
   * ⚠ **En un efecto y NO durante el render.** Llamar a `setDatos` en el cuerpo del
   * componente es el camino corto al bucle infinito: cada render vuelve a cumplir la
   * condición. Y sólo se pone si el campo sigue vacío — quien ya escribió otra
   * agencia no puede ver cómo se la pisan al terminar de cargar la pantalla.
   */
  useEffect(() => {
    const razon = empresa.data === undefined ? "" : comoSeLlama(empresa.data)
    if (razon === "") return
    setDatos((previos) =>
      previos.tipo === "POSICIONAMIENTO" && previos.agencia === ""
        ? { ...previos, agencia: razon }
        : previos,
    )
  }, [empresa.data])

  // El marco de la última impresión se va con la pantalla.
  useEffect(() => soltarImpresion, [])

  /** Componer el PDF, registrarlo y, sólo si quedó registrado, imprimirlo. */
  const imprimirSolicitud = useMutation({
    mutationFn: async (solicitud: DatosPosicionamiento) => {
      const pdf = await pdfDePosicionamiento(solicitud)
      const forma = new FormData()
      forma.append(
        "archivo",
        new Blob([pdf], { type: "application/pdf" }),
        "solicitud-posicionamiento.pdf",
      )
      forma.append("datos", JSON.stringify(solicitud))
      const fila = await pedir<Impresion>("/api/v1/impresiones", { method: "POST", body: forma })
      return { fila, pdf }
    },
    onSuccess: ({ pdf }) => {
      imprimirPdf(pdf)
      void clienteQuery.invalidateQueries({ queryKey: ["impresiones"] })
    },
  })

  function imprimir() {
    if (datos.tipo === "POSICIONAMIENTO") imprimirSolicitud.mutate(datos)
    else window.print()
  }

  function empezarDeNuevo(tipo: TipoPlantilla) {
    // ⚠ Se empieza en blanco al cambiar de formato: los dos comparten cabecera y pie
    // pero nada más, y arrastrar lo tecleado dejaría media hoja rellena con datos del
    // otro trámite sin que nada lo dijera.
    setDatos(enBlanco(tipo))
    imprimirSolicitud.reset()
  }

  const hecha = imprimirSolicitud.data

  return (
    /* ⚠ **El papel se le da a TODA la ruta, no sólo a la hoja**, y cambia con el
       formato elegido: ver la nota de `globales.css`. Puesto sólo en la hoja salen dos
       páginas —la del formato y una en blanco detrás, que la genera el resto del
       documento—, y con el papel del otro formato el A4 se parte en dos. */
    <div className={esGuia ? "ruta-guia" : "ruta-plantilla"}>
      <header className="no-print mb-6">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <FileSignature size={20} aria-hidden={true} />
          Plantillas oficiales
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          Los formatos que la agencia rellena y entrega, a tamaño real. Se escribe encima y se
          imprime: lo que ves es exactamente lo que sale. Los dos de la Aduana Principal de Puerto
          Cabello van en A4; la guía de despacho, en carta.
        </p>
      </header>

      <div className="no-print mb-6 flex flex-wrap gap-2">
        {(Object.keys(ETIQUETAS) as TipoPlantilla[]).map((clave) => (
          <button
            key={clave}
            type="button"
            onClick={() => clave !== datos.tipo && empezarDeNuevo(clave)}
            aria-pressed={datos.tipo === clave}
            className={
              datos.tipo === clave
                ? "boton-primario h-9 px-3 text-sm"
                : "boton-secundario h-9 px-3 text-sm"
            }
          >
            {ETIQUETAS[clave]}
          </button>
        ))}
      </div>

      <div className="no-print mb-4 flex flex-wrap items-center gap-3">
        <Boton variante="primario" onClick={imprimir} disabled={imprimirSolicitud.isPending}>
          <Printer size={15} aria-hidden={true} />
          {imprimirSolicitud.isPending ? "Guardando…" : "Imprimir"}
        </Boton>
        <button
          type="button"
          className="boton-secundario h-9 px-3 text-sm"
          onClick={() => empezarDeNuevo(datos.tipo)}
        >
          Vaciar
        </button>
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          En el diálogo, deja la escala en <strong>100 %</strong>, elige{" "}
          <strong>{esGuia ? "Carta" : "A4"}</strong> y activa los gráficos de fondo. «Ajustar al
          área de impresión» es lo que deforma el formato.
        </p>
      </div>

      {imprimirSolicitud.isError && (
        <p role="alert" className="no-print mb-4 text-sm" style={{ color: "var(--destructive)" }}>
          No se imprimió: el registro no se pudo guardar ({imprimirSolicitud.error.message}). Sin
          registro no sale el papel; vuelve a intentarlo.
        </p>
      )}
      {hecha !== undefined && (
        <p role="status" className="no-print mb-4 text-sm" style={{ color: "var(--fg-muted)" }}>
          Registrada el <span className="tabular-nums">{comoFechaHora(hecha.fila.creado_en)}</span>.
          Si no se abrió el diálogo de impresión,{" "}
          <button
            type="button"
            className="py-1 underline underline-offset-2"
            onClick={() => abrirPdf(hecha.pdf)}
          >
            abre el PDF
          </button>{" "}
          e imprímelo desde ahí.
        </p>
      )}

      {/* ⚠ **A escala real y con desplazamiento si no cabe, en vez de encogerla:** la
          hoja mide 210 mm y en una pantalla estrecha no entra. Reducirla la haría
          cómoda de mirar y dejaría de ser lo que se imprime, que es lo único que
          importa aquí. */}
      <div className="no-print overflow-auto pb-4">
        <div className="pieza-impresa-previa" style={{ width: "fit-content" }}>
          <VistaPlantilla datos={datos} onCambio={setDatos} />
        </div>
      </div>

      {datos.tipo === "POSICIONAMIENTO" ? (
        <>
          <RegistroImpresiones />
          {/* ⚠ Con Ctrl+P la solicitud saldría de la pantalla y sin registro. Lo que
              sale en su lugar es esto: una hoja que dice cómo se imprime. */}
          <p className="solo-impresion">
            La Solicitud de Posicionamiento se imprime con el botón «Imprimir» del sistema, que es
            el que guarda el registro con su PDF.
          </p>
        </>
      ) : (
        /* ⚠ **La hoja que se RELLENA no es la que se imprime.** Es el mismo componente
           con los mismos datos, pero una hoja de 297 mm dentro del armazón salía con
           una segunda página en blanco detrás: algo de la cadena de contenedores añade
           una fracción de milímetro y el A4 no admite ni una. El pliego vive fuera del
           flujo de la pantalla y ahí no hay cadena que sume. */
        <PliegoImpresion
          pieza={<VistaPlantilla datos={datos} onCambio={setDatos} />}
          copias={1}
          porHoja={1}
          guias={false}
        />
      )}
    </div>
  )
}
